import { useAppService, useSelectedOrgId } from "@fero/hooks";
import { AppRoutes } from "@fero/utils";
import type { DbKey, WithDbId } from "adl-gen/common/db";
import type {
  NominatedPurchaserDetailsView,
  NominatedPurchaserIndexItemView,
  ProductSalePriceInfo,
  StorageLocationView,
} from "adl-gen/ferovinum/app/api";
import type {
  PurchaseRequestSalePriceType,
  StorageLocation,
} from "adl-gen/ferovinum/app/db";
import { useUiConfig } from "adl-service/hooks/use-ui-config";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useLoadingDataState } from "utils/hooks/use-loading-data";
import { assertNotUndefined } from "utils/hx/util/types";
import { ensure } from "utils/null-and-undefined-utils";
import { isValidNumber } from "utils/ts-utils";
import { isLoaded } from "utils/utility-types";
import type {
  FormStorageLocProductSale,
  PurchaseRequestFlowState,
  StorageListing,
  StorageLocationProductSale,
} from "../purchase-request-types";
import { OrganisationPurchaseRequestSetupPageView } from "./organisation-purchase-request-setup-page-view";

export interface SetupPurchaserRequestFormValues<
  Purchaser extends
    NominatedPurchaserIndexItemView = NominatedPurchaserDetailsView,
> {
  purchaser?: Purchaser;
  products: FormStorageLocProductSale[];
  bondedSale: boolean;
  dealDiscountAmount: string | null;
  salePriceType: PurchaseRequestSalePriceType;
  poFile?: File;
}
export const OrganisationPurchaseRequestSetupPage = () => {
  const location = useLocation();
  const history = useHistory();
  const service = useAppService();
  const organisationId = assertNotUndefined(useSelectedOrgId());
  const { showProductionOrder } = useUiConfig();
  const purchaseRequestFlowState = location.state as
    | PurchaseRequestFlowState
    | undefined;
  const [activeStorageLocation, setActiveStorageLocation] =
    useState<WithDbId<StorageLocation>>();
  const [storageLocations, setStorageLocations] = useState<
    WithDbId<StorageLocation>[]
  >([]);
  const [selectedPurchaser, setSelectedPurchaser] =
    useState<NominatedPurchaserDetailsView | null>(
      purchaseRequestFlowState?.setup?.purchaser ?? null,
    );

  const loadFinishedProductListings = useCallback(async () => {
    const finishedProductListings =
      await service.getThirdPartySaleFinishedProductListings({
        organisationId: organisationId,
        searchTerm: null,
      });
    const finishedProductListingsArr: StorageListing[] = [];
    return finishedProductListings.reduce((acc, cur) => {
      acc.push({
        storageLocation: cur.key,
        products: cur.value,
      });
      return acc;
    }, finishedProductListingsArr);
  }, [service, organisationId]);

  const loadFinishableProductListings = useCallback(async () => {
    if (!showProductionOrder) {
      return [];
    }

    const finishableProductListings =
      await service.getThirdPartySaleFinishableProductListings({
        organisationId: organisationId,
        searchTerm: null,
      });

    const finishableProductListingsArr: StorageListing[] = [];
    return finishableProductListings.reduce((acc, cur) => {
      acc.push({
        storageLocation: cur.key,
        products: cur.value,
      });
      return acc;
    }, finishableProductListingsArr);
  }, [showProductionOrder, service, organisationId]);

  const loadProductSalePrices = useCallback(async () => {
    if (!selectedPurchaser?.nominatedPurchaserId) {
      return [];
    }

    return await service.getProductSalePrices({
      nominatedPurchaserId: selectedPurchaser?.nominatedPurchaserId,
    });
  }, [service, selectedPurchaser?.nominatedPurchaserId]);

  const [loadingFinishedProductListings] = useLoadingDataState<
    StorageListing[]
  >(loadFinishedProductListings);

  const [loadingFinishableProductListings] = useLoadingDataState<
    StorageListing[]
  >(loadFinishableProductListings);

  const [loadingProductSalePrices] = useLoadingDataState<
    ProductSalePriceInfo[]
  >(loadProductSalePrices);

  useEffect(() => {
    let newLocs: WithDbId<StorageLocation>[] = [];
    if (isLoaded(loadingFinishableProductListings)) {
      newLocs = [
        ...newLocs,
        ...loadingFinishableProductListings.value.map((e) => e.storageLocation),
      ];
    }
    if (isLoaded(loadingFinishedProductListings)) {
      newLocs = [
        ...newLocs,
        ...loadingFinishedProductListings.value.map((e) => e.storageLocation),
      ];
    }

    const uniqueIdsSet = new Set<DbKey<StorageLocation>>(
      storageLocations.map((e) => e.id),
    );
    const newStorageLocations: WithDbId<StorageLocation>[] = [
      ...storageLocations,
    ];
    newLocs.forEach((e) => {
      if (!uniqueIdsSet.has(e.id)) {
        uniqueIdsSet.add(e.id);
        newStorageLocations.push(e);
      }
    });

    if (newStorageLocations.length === storageLocations.length) {
      // Break claus - If this is an empty set we can assume that all
      // locations are loaded, and we don't need to do anything
      return;
    }

    setStorageLocations(newStorageLocations);
    const previousStorageLocation =
      purchaseRequestFlowState?.setup?.storageLocation;
    const maybeStorageLocation = newStorageLocations.find(
      (loc) => loc.id === previousStorageLocation?.id,
    );
    if (maybeStorageLocation) {
      setActiveStorageLocation(maybeStorageLocation);
    }
  }, [
    loadingFinishableProductListings,
    loadingFinishedProductListings,
    purchaseRequestFlowState?.setup?.storageLocation,
    storageLocations,
  ]);

  /**
   * To avoid rewriting old trade sale flow, we will simply map the storage location to it's view here
   */
  const mapStorageLocationToStorageLocationView = (
    sl?: WithDbId<StorageLocation>,
  ): StorageLocationView | undefined => {
    if (!sl) {
      return undefined;
    }

    return {
      id: sl.id,
      storageLocationName: sl.value.locationName,
      addressLine1: sl.value.addressLine1,
      addressLine2: sl.value.addressLine2,
      availableForShipping: sl.value.hasD2cFulfilment,
      country: sl.value.country,
      postCode: sl.value.postCode,
      deliveryCountries: sl.value.deliveryCountries,
      deliveryInstructionConfig: sl.value.deliveryInstructionConfig,
      endpoint: sl.value.endpoint,
      requiresAccountCodeForInWarehouseTransfer:
        sl.value.endpoint?.kind === "vision",
      siteCode: sl.value.siteCode,
      town: sl.value.town,
      warehouseGroup: sl.value.warehouseGroup,
    };
  };

  const onNext = async (values: SetupPurchaserRequestFormValues) => {
    const filterSelectedProducts = (
      products: FormStorageLocProductSale[],
    ): StorageLocationProductSale[] => {
      return products
        .filter(
          (p) =>
            (isValidNumber(p.paidUnits) && isValidNumber(p.unitPrice)) ||
            isValidNumber(p.freeUnits),
        )
        .map((p) => ({
          ...p,
          paidUnits: p.paidUnits ? Number(p.paidUnits) : 0,
          freeUnits:
            p.freeUnits !== undefined ? Number(p.freeUnits) : undefined,
          unitPrice: p.unitPrice ? Number(p.unitPrice) : 0,
          priceDiscount:
            p.priceDiscount !== undefined ? Number(p.priceDiscount) : undefined,
        }));
    };
    const newState: PurchaseRequestFlowState = {
      ...purchaseRequestFlowState,
      // Note: For now reset the production plan if there was any
      // too much hassle to check if the previous plan is still compatible or not if they change the sources
      productionPlan: undefined,
      setup: {
        storageLocation: mapStorageLocationToStorageLocationView(
          activeStorageLocation,
        ),
        purchaser: ensure(values.purchaser),
        products: filterSelectedProducts(values.products),
        dealDiscountAmount: values.dealDiscountAmount,
        bondedSale: values.bondedSale,
        // Sale price type is only relevant for out of bond sales. For in bond sales, the price is always excluding duty
        salePriceType: values.bondedSale
          ? "exDutyAndVat"
          : values.salePriceType,
        poFile: values.poFile,
      },
    };
    if (extractNonFinishedProducts(newState.setup?.products ?? []).length > 0) {
      history.push(
        AppRoutes.OrganisationPurchaseRequestProductionPlan,
        newState,
      );
    } else {
      // reset production plan as it might not be applicable anymore
      newState.productionPlan = undefined;
      history.push(
        AppRoutes.OrganisationPurchaseRequestDeliveryOptions,
        newState,
      );
    }
  };

  const onChangeStorageLocation = async (
    loc: WithDbId<StorageLocation> | null,
  ) => {
    if (purchaseRequestFlowState?.setup) {
      purchaseRequestFlowState.setup.products = [];
    }
    setActiveStorageLocation(loc ?? undefined);
  };

  return (
    <OrganisationPurchaseRequestSetupPageView
      onPurchaserSelected={setSelectedPurchaser}
      activeStorageLocation={activeStorageLocation}
      storageLocations={storageLocations}
      onChangeStorageLocation={onChangeStorageLocation}
      initialValues={purchaseRequestFlowState?.setup}
      onNext={onNext}
      showProductionOrders={showProductionOrder}
      finishableProducts={loadingFinishableProductListings}
      finishedProducts={loadingFinishedProductListings}
      productsDefaultSalePrices={loadingProductSalePrices}
    />
  );
};

export function extractNonFinishedProducts(
  products: StorageLocationProductSale[],
): StorageLocationProductSale[] {
  return products
    .map((p) => ({ ...p, ...splitQuantities(p).produced }))
    .filter((p) => p.paidUnits + (p.freeUnits ?? 0) > 0);
}

interface ProductQuantities {
  paidUnits: number;
  freeUnits?: number;
}

function splitQuantities({
  paidUnits,
  freeUnits,
  totalAvailable,
  totalProducible,
}: StorageLocationProductSale): {
  finished: ProductQuantities;
  produced: ProductQuantities;
} {
  // the split must add up to paidUnits
  const paidQtySplit =
    paidUnits <= totalAvailable
      ? { finished: paidUnits, produced: 0 }
      : { finished: totalAvailable, produced: paidUnits - totalAvailable };

  const availableAfterPaidQty = totalAvailable - paidQtySplit.finished;
  // the split must add up to freeUnits
  const freeQtySplit =
    freeUnits == undefined
      ? undefined
      : freeUnits <= availableAfterPaidQty
        ? { finished: freeUnits, produced: 0 }
        : {
            finished: availableAfterPaidQty,
            produced: freeUnits - availableAfterPaidQty,
          };

  if (paidQtySplit.produced + (freeQtySplit?.produced ?? 0) > totalProducible) {
    throw new Error("Produced quantity exceeds total producible quantity");
  }
  return {
    finished: {
      paidUnits: paidQtySplit.finished,
      freeUnits: freeQtySplit?.finished,
    },
    produced: {
      paidUnits: paidQtySplit.produced,
      freeUnits: freeQtySplit?.produced,
    },
  };
}
